// src/services/toolsService.js
// Comprehensive Tools Service with Marketplace, Installation & Permissions
import { 
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, writeBatch, limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';

// ============================================================
// CONSTANTS & ENUMS
// ============================================================

export const ToolCategory = {
  PRODUCTIVITY: 'productivity',
  FINANCE: 'finance',
  EDUCATION: 'education',
  PLANNING: 'planning',
  ANALYSIS: 'analysis',
  DOCUMENTS: 'documents',
  LOGISTICS: 'logistics',
  LEGAL: 'legal',
  HEALTHCARE: 'healthcare',
  CUSTOM: 'custom'
};

export const ToolType = {
  INTERNAL: 'INTERNAL',     // Embedded HTML/iframe
  EXTERNAL: 'EXTERNAL',     // External links
  BUILT_IN: 'BUILT_IN',     // Platform-provided React components
  GENERATOR: 'GENERATOR',
  CALCULATOR: 'CALCULATOR',
  PLANNER: 'PLANNER',
  TRACKER: 'TRACKER'
};

export const ToolPricing = {
  FREE: 'free',
  PAID: 'paid',
  SUBSCRIPTION: 'subscription',
  FREEMIUM: 'freemium'  // Free with paid features
};

export const InstallationStatus = {
  INSTALLED: 'installed',
  PENDING: 'pending',      // Awaiting payment
  TRIAL: 'trial',          // Trial period
  EXPIRED: 'expired',      // Subscription expired
  DISABLED: 'disabled'     // Manually disabled
};

// ============================================================
// TOOL CATALOG (Built-in Tools Registry)
// This defines all available built-in tools in the marketplace
// ============================================================

export const TOOL_CATALOG = {
  'school-strategic': {
    id: 'school-strategic',
    slug: 'school-strategic',
    name: 'School Strategic Management',
    description: 'SSE Assessment & School Improvement Planning for South African schools based on DBE WSE Policy',
    longDescription: `Comprehensive strategic management tool for school leaders. Conduct School Self-Evaluations (SSE) across all 9 WSE areas, identify priority areas, and create data-driven School Improvement Plans (SIP) with quarterly progress tracking.`,
    category: ToolCategory.EDUCATION,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREE,
    price: 0,
    currency: 'ZAR',
    featured: true,
    tags: ['education', 'school', 'management', 'sse', 'sip', 'south-africa', 'dbe'],
    icon: 'Target',
    coverImage: '/tools/school-strategic-cover.jpg',
    screenshots: [],
    dataCollections: ['sse-assessments', 'sip-plans'],
    permissions: ['read', 'write', 'delete', 'export'],
    requirements: [],
    changelog: [
      { version: '1.0.0', date: '2024-01-01', changes: ['Initial release'] }
    ]
  },
  
  'invoice-generator': {
    id: 'invoice-generator',
    slug: 'invoice-generator',
    name: 'Invoice Generator',
    description: 'Create professional invoices, quotes, and track payments',
    longDescription: `Comprehensive invoicing solution for small businesses. Create professional invoices and quotations, manage clients, track payments, and configure tax settings. Features include:

• Professional invoice & quotation templates
• Client management with contact details
• Automatic document numbering
• Multiple currency support (ZAR, USD, EUR, GBP)
• Configurable tax rates and payment terms
• Line items with discounts and tax options
• Convert quotations to invoices
• Payment status tracking
• PDF export capability
• Bank details integration`,
    category: ToolCategory.FINANCE,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREEMIUM,
    price: 0,
    premiumPrice: 99,
    currency: 'ZAR',
    featured: true,
    tags: ['finance', 'invoicing', 'business', 'payments', 'quotes', 'billing', 'accounting'],
    icon: 'Receipt',
    dataCollections: ['invoices', 'quotations', 'clients'],
    permissions: ['read', 'write', 'delete', 'export'],
    limits: {
      free: { documentsPerMonth: 10, clients: 20 },
      premium: { documentsPerMonth: -1, clients: -1 }  // Unlimited
    },
    changelog: [
      { version: '1.0.0', date: '2024-01-01', changes: ['Initial release with invoices, quotations, and client management'] }
    ]
  },

  'business-model-canvas': {
    id: 'business-model-canvas',
    slug: 'business-model-canvas',
    name: 'Business Model Canvas',
    description: 'AI-powered Business Model Canvas using Strategyzer methodology',
    longDescription: `Create comprehensive Business Model Canvases with AI assistance. Based on the proven Strategyzer model, this tool guides you through all 9 building blocks:

• Value Propositions - Define your unique value
• Customer Segments - Identify target customers
• Channels - Plan how to reach customers
• Customer Relationships - Build retention strategies
• Revenue Streams - Map monetization models
• Key Resources - Identify critical assets
• Key Activities - Define core operations
• Key Partnerships - Plan strategic alliances
• Cost Structure - Analyze expenses

AI generates content based on your answers, which you can edit and refine. Previous sections inform subsequent ones for coherent strategy development.`,
    category: ToolCategory.PLANNING,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREEMIUM,
    price: 0,
    premiumPrice: 149,
    currency: 'ZAR',
    featured: true,
    tags: ['business', 'strategy', 'planning', 'canvas', 'startup', 'ai', 'strategyzer'],
    icon: 'Grid',
    dataCollections: ['business-model-canvases'],
    permissions: ['read', 'write', 'delete', 'export'],
    requirements: ['Gemini API Key'],
    limits: {
      free: { canvases: 3, aiGenerationsPerDay: 10 },
      premium: { canvases: -1, aiGenerationsPerDay: -1 }
    },
    changelog: [
      { version: '1.0.0', date: '2024-01-01', changes: ['Initial release with all 9 BMC sections and AI generation'] }
    ]
  },

  'operational-planner': {
    id: 'operational-planner',
    slug: 'operational-planner',
    name: 'Operational Planner',
    description: 'Create and manage operational plans with multiple views',
    longDescription: `Comprehensive operational planning tool for organizations. Features include:

- Create multiple operational plans by year
- Sortable horizontal cards view with drag-and-drop
- Year Planner with monthly and weekly views
- Grid and list layouts for all planner views
- Status tracking: Planned, In Progress, Completed
- Progress updates with notes and history
- Completion indicators: Successful, Partial, Not Successful
- Priority levels and date ranges
- Tags and assignee tracking`,
    category: ToolCategory.PLANNING,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREEMIUM,
    price: 0,
    premiumPrice: 199,
    currency: 'ZAR',
    featured: true,
    tags: ['planning', 'operations', 'calendar', 'year-planner', 'project', 'tracking', 'progress'],
    icon: 'CalendarRange',
    dataCollections: ['operational-plans', 'operational-items'],
    permissions: ['read', 'write', 'delete'],
    limits: {
      free: { plans: 3, itemsPerPlan: 50 },
      premium: { plans: -1, itemsPerPlan: -1 }
    },
    changelog: [
      { version: '1.0.0', date: '2024-01-01', changes: ['Initial release with cards and year planner views'] }
    ]
  },

  'team-manager': {
    id: 'team-manager',
    slug: 'team-manager',
    name: 'Team Manager',
    description: 'Manage team members, tasks, engagements, and KPI performance',
    longDescription: `Comprehensive team management solution for managers and team leads. Features include:

- Team member profiles with photos and details
- Custom KPIs per member with targets
- Task assignment to individuals or groups
- Engagement recording with KPI/task references
- Weekly KPI scoring with star ratings
- Performance tracking and history
- Activity dashboard with stats`,
    category: ToolCategory.PRODUCTIVITY,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREEMIUM,
    price: 0,
    premiumPrice: 149,
    currency: 'ZAR',
    featured: true,
    tags: ['team', 'management', 'kpi', 'performance', 'tasks', 'hr', 'engagement'],
    icon: 'Users',
    dataCollections: ['team-members', 'team-tasks', 'team-engagements', 'kpi-scores'],
    permissions: ['read', 'write', 'delete', 'export'],
    limits: {
      free: { members: 10, tasksPerMonth: 50 },
      premium: { members: -1, tasksPerMonth: -1 }
    },
    changelog: [
      { version: '1.0.0', date: '2024-01-01', changes: ['Initial release with members, tasks, engagements, and KPI scoring'] }
    ]
  },

  'project-timeline': {
    id: 'project-timeline',
    slug: 'timelines',
    name: 'Project Timeline Planner',
    description: 'Visual project planning with Gantt charts and milestones',
    longDescription: `Plan and visualize projects with interactive Gantt charts. Set milestones, dependencies, and track progress. Perfect for project managers and team leads.`,
    category: ToolCategory.PLANNING,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.PAID,
    price: 149,
    currency: 'ZAR',
    featured: false,
    tags: ['project', 'planning', 'gantt', 'timeline'],
    icon: 'Calendar',
    dataCollections: ['projects', 'milestones', 'tasks'],
    permissions: ['read', 'write', 'delete', 'export', 'share']
  },

  'risk-register': {
    id: 'risk-register',
    slug: 'risk-registers',
    name: 'Risk Register',
    description: 'Identify, assess, and mitigate organizational risks',
    longDescription: `Comprehensive risk management tool. Identify risks, assess probability and impact, develop mitigation strategies, and track risk status over time.`,
    category: ToolCategory.ANALYSIS,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREE,
    price: 0,
    currency: 'ZAR',
    featured: false,
    tags: ['risk', 'management', 'compliance', 'governance'],
    icon: 'AlertTriangle',
    dataCollections: ['risk-registers', 'risk-assessments'],
    permissions: ['read', 'write', 'delete', 'export']
  },

  'budget-planner': {
    id: 'budget-planner',
    slug: 'budgets',
    name: 'Budget Planner',
    description: 'Create and track budgets with variance analysis',
    longDescription: `Plan annual budgets, track actual spending, and analyze variances. Supports multiple budget categories, departments, and reporting periods.`,
    category: ToolCategory.FINANCE,
    type: ToolType.BUILT_IN,
    version: '1.0.0',
    author: 'ParrotBooks',
    pricing: ToolPricing.FREEMIUM,
    price: 0,
    premiumPrice: 79,
    currency: 'ZAR',
    featured: false,
    tags: ['budget', 'finance', 'planning', 'tracking'],
    icon: 'PieChart',
    dataCollections: ['budgets', 'transactions'],
    permissions: ['read', 'write', 'delete', 'export']
  }
};

// ============================================================
// TOOLS SERVICE
// ============================================================

export const toolsService = {

  // ============================================================
  // MARKETPLACE - Browse & Discover Tools
  // ============================================================

  /**
   * Get all tools available in the marketplace
   */
  getMarketplaceCatalog(options = {}) {
    let tools = Object.values(TOOL_CATALOG);

    // Filter by category
    if (options.category) {
      tools = tools.filter(t => t.category === options.category);
    }

    // Filter by pricing
    if (options.pricing) {
      tools = tools.filter(t => t.pricing === options.pricing);
    }

    // Filter by search query
    if (options.search) {
      const query = options.search.toLowerCase();
      tools = tools.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.tags?.some(tag => tag.includes(query))
      );
    }

    // Sort
    if (options.sort === 'name') {
      tools.sort((a, b) => a.name.localeCompare(b.name));
    } else if (options.sort === 'price') {
      tools.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else {
      // Default: featured first, then by name
      tools.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return a.name.localeCompare(b.name);
      });
    }

    return tools;
  },

  /**
   * Get tool details from catalog
   */
  getToolFromCatalog(toolId) {
    return TOOL_CATALOG[toolId] || null;
  },

  /**
   * Get tools by category
   */
  getToolsByCategory(category) {
    return this.getMarketplaceCatalog({ category });
  },

  /**
   * Get featured tools
   */
  getFeaturedTools() {
    return Object.values(TOOL_CATALOG).filter(t => t.featured);
  },

  /**
   * Get free tools
   */
  getFreeTools() {
    return Object.values(TOOL_CATALOG).filter(t => 
      t.pricing === ToolPricing.FREE || t.pricing === ToolPricing.FREEMIUM
    );
  },

  // ============================================================
  // TOOL INSTALLATION - Per-Book/Workspace
  // Collection: pb-books/{bookId}/installed-tools/{toolId}
  // ============================================================

  /**
   * Get all installed tools for a book/workspace
   */
  async getInstalledTools(bookId, workspaceIds = null) {
    try {
      const toolsRef = collection(db, 'pb-books', bookId, 'installed-tools');
      const currentUser = auth.currentUser;
      let q;
      
      if (workspaceIds && workspaceIds.length > 0) {
        // Filter by workspace(s) user has access to
        q = query(
          toolsRef,
          where('workspaceId', 'in', workspaceIds),
          orderBy('installedAt', 'desc')
        );
      } else if (currentUser) {
        // Fallback: only get tools installed by current user or in default workspace
        q = query(
          toolsRef,
          where('workspaceId', 'in', ['default', `personal-${currentUser.uid}`]),
          orderBy('installedAt', 'desc')
        );
      } else {
        // Not authenticated - return empty
        return [];
      }
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const installation = { id: doc.id, ...doc.data() };
        // Merge with catalog data for full tool info using toolId field
        const catalogData = TOOL_CATALOG[installation.toolId] || {};
        return { ...catalogData, ...installation, installed: true };
      });
    } catch (error) {
      console.error('Error getting installed tools:', error);
      return [];
    }
  },

  /**
   * Check if a tool is installed
   */
  async isToolInstalled(bookId, toolId) {
    try {
      const toolRef = doc(db, 'pb-books', bookId, 'installed-tools', toolId);
      const docSnap = await getDoc(toolRef);
      return docSnap.exists() && docSnap.data().status === InstallationStatus.INSTALLED;
    } catch (error) {
      console.error('Error checking tool installation:', error);
      return false;
    }
  },

  /**
   * Check if a tool is installed to a specific workspace
   */
  async isToolInstalledToWorkspace(bookId, toolId, workspaceId) {
    try {
      const toolsRef = collection(db, 'pb-books', bookId, 'installed-tools');
      const q = query(
        toolsRef,
        where('toolId', '==', toolId),
        where('workspaceId', '==', workspaceId),
        where('status', '==', InstallationStatus.INSTALLED)
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking workspace tool installation:', error);
      return false;
    }
  },

  /**
   * Install a tool to a book/workspace
   * @param {string} bookId - The book ID
   * @param {string} toolId - The tool ID from catalog
   * @param {string} workspaceId - The workspace ID to install to
   * @param {object} options - Additional options
   */
  async installTool(bookId, toolId, workspaceId = null, options = {}) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const catalogTool = TOOL_CATALOG[toolId];
      if (!catalogTool) throw new Error('Tool not found in catalog');

      // Check if already installed
      const existing = await this.isToolInstalledToWorkspace(bookId, toolId, workspaceId);
      if (existing) throw new Error('Tool already installed to this workspace');

      // Determine installation status based on pricing
      let status = InstallationStatus.INSTALLED;
      let trialEndsAt = null;

      if (catalogTool.pricing === ToolPricing.PAID) {
        if (!options.paymentConfirmed) {
          // Could implement trial period
          status = InstallationStatus.TRIAL;
          trialEndsAt = new Date();
          trialEndsAt.setDate(trialEndsAt.getDate() + 14); // 14-day trial
        }
      }

      const installationData = {
        toolId,
        bookId,
        workspaceId,  // Workspace association for permissions
        status,
        installedBy: currentUser.uid,
        installedByEmail: currentUser.email,
        installedAt: serverTimestamp(),
        trialEndsAt,
        settings: options.settings || {},
        permissions: catalogTool.permissions || ['read', 'write'],
        tier: options.tier || 'free',  // For freemium tools
        // Owner customization fields
        customName: null,
        customDescription: null,
        isVisible: true,
        isPinned: false,
        notes: ''
      };

       const toolsCollectionRef = collection(db, 'pb-books', bookId, 'installed-tools');
        const toolRef = doc(toolsCollectionRef);
        
        await setDoc(toolRef, {
          ...installationData,
          id: toolRef.id
        });

        return { id: toolRef.id, ...installationData, ...catalogTool };
    } catch (error) {
      console.error('Error installing tool:', error);
      throw error;
    }
  },

  /**
   * Uninstall a tool from a book/workspace
   */
  async uninstallTool(bookId, installationId, options = {}) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      // Get installation to find toolId and workspaceId
      const toolRef = doc(db, 'pb-books', bookId, 'installed-tools', installationId);
      const toolSnap = await getDoc(toolRef);
      
      if (!toolSnap.exists()) throw new Error('Installation not found');
      
      const installation = toolSnap.data();

      // Optionally delete tool data for this workspace
      if (options.deleteData && installation.workspaceId) {
        const catalogTool = TOOL_CATALOG[installation.toolId];
        if (catalogTool?.dataCollections) {
          for (const collectionSlug of catalogTool.dataCollections) {
            await this.deleteAllToolData(bookId, collectionSlug, installation.workspaceId);
          }
        }
      }

      await deleteDoc(toolRef);

      return true;
    } catch (error) {
      console.error('Error uninstalling tool:', error);
      throw error;
    }
  },

  /**
   * Update tool installation settings
   */
  async updateToolInstallation(bookId, toolId, updates) {
    try {
      const toolRef = doc(db, 'pb-books', bookId, 'installed-tools', toolId);
      await updateDoc(toolRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating tool installation:', error);
      throw error;
    }
  },

  /**
   * Upgrade tool tier (freemium -> premium)
   */
  async upgradeTool(bookId, toolId, newTier, paymentDetails = {}) {
    try {
      const toolRef = doc(db, 'pb-books', bookId, 'installed-tools', toolId);
      await updateDoc(toolRef, {
        tier: newTier,
        status: InstallationStatus.INSTALLED,
        upgradedAt: serverTimestamp(),
        paymentReference: paymentDetails.reference || null
      });
      return true;
    } catch (error) {
      console.error('Error upgrading tool:', error);
      throw error;
    }
  },

  // ============================================================
  // CUSTOM TOOLS - User-Created Tools
  // Collection: pb-books/{bookId}/custom-tools/{toolId}
  // ============================================================

  async getCustomTools(bookId) {
    try {
      const toolsRef = collection(db, 'pb-books', bookId, 'custom-tools');
      const q = query(toolsRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), isCustom: true }));
    } catch (error) {
      console.error('Error getting custom tools:', error);
      return [];
    }
  },

  async saveCustomTool(bookId, tool) {
    try {
      const toolId = tool.id || doc(collection(db, 'pb-books', bookId, 'custom-tools')).id;
      const toolRef = doc(db, 'pb-books', bookId, 'custom-tools', toolId);
      
      const toolData = {
        ...tool,
        id: toolId,
        bookId,
        isCustom: true,
        createdBy: tool.createdBy || auth.currentUser?.uid,
        updatedAt: serverTimestamp()
      };
      
      if (!tool.id) {
        toolData.createdAt = serverTimestamp();
      }
      
      await setDoc(toolRef, toolData, { merge: true });
      return { id: toolId, ...toolData };
    } catch (error) {
      console.error('Error saving custom tool:', error);
      throw error;
    }
  },

  async deleteCustomTool(bookId, toolId) {
    try {
      const toolRef = doc(db, 'pb-books', bookId, 'custom-tools', toolId);
      await deleteDoc(toolRef);
      return true;
    } catch (error) {
      console.error('Error deleting custom tool:', error);
      throw error;
    }
  },

  // ============================================================
  // TOOL DATA - Outputs Generated by Tools
  // Collection: pb-books/{bookId}/tool-data/{toolSlug}/items/{itemId}
  // ============================================================

  /**
   * Get all data for a tool (filtered by workspace)
   */
  async getToolData(bookId, toolSlug, workspaceId = 'default', options = {}) {
    try {
      const currentUser = auth.currentUser;
      const dataRef = collection(db, 'pb-books', bookId, 'tool-data', toolSlug, 'items');
      
      // Build query with workspace filter
      let q;
      if (workspaceId) {
        q = query(
          dataRef, 
          where('workspaceId', '==', workspaceId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(dataRef, orderBy('createdAt', 'desc'));
      }
      
      if (options.status) {
        q = query(
          dataRef, 
          where('workspaceId', '==', workspaceId),
          where('status', '==', options.status), 
          orderBy('createdAt', 'desc')
        );
      }
      
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      
      // Filter by permissions if needed
      return snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(item => {
          // Creator always has access
          if (item.createdBy === currentUser?.uid) return true;
          // Check visibility
          if (item.visibility === 'public') return true;
          // Check shared access
          if (item.sharedWith?.includes(currentUser?.uid)) return true;
          if (item.sharedWith?.includes(currentUser?.email)) return true;
          // Default: allow if no specific restrictions
          if (!item.visibility && !item.sharedWith) return true;
          return false;
        });
    } catch (error) {
      console.error(`Error getting ${toolSlug} data:`, error);
      return [];
    }
  },

  /**
   * Get single tool data item
   */
  async getToolDataItem(bookId, toolSlug, itemId) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'tool-data', toolSlug, 'items', itemId);
      const docSnap = await getDoc(itemRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error(`Error getting ${toolSlug} item:`, error);
      return null;
    }
  },

  /**
   * Save tool data with workspace isolation
   */
  async saveToolData(bookId, toolSlug, data, workspaceId = 'default') {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Authentication required');

      const itemsRef = collection(db, 'pb-books', bookId, 'tool-data', toolSlug, 'items');
      const itemId = data.id || doc(itemsRef).id;
      const itemRef = doc(db, 'pb-books', bookId, 'tool-data', toolSlug, 'items', itemId);
      
      const itemData = {
        ...data,
        id: itemId,
        toolSlug,
        bookId,
        workspaceId, // Store workspace for filtering
        createdBy: data.createdBy || currentUser.uid,
        creatorEmail: data.creatorEmail || currentUser.email,
        visibility: data.visibility || 'workspace',
        sharedWith: data.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!data.id) {
        itemData.createdAt = serverTimestamp();
      }
      
      await setDoc(itemRef, itemData, { merge: true });
      return { id: itemId, ...itemData };
    } catch (error) {
      console.error(`Error saving ${toolSlug} item:`, error);
      throw error;
    }
  },

  /**
   * Delete tool data item
   */
  async deleteToolData(bookId, toolSlug, itemId, workspaceId = 'default') {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'tool-data', toolSlug, 'items', itemId);
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error(`Error deleting ${toolSlug} item:`, error);
      throw error;
    }
  },

  /**
   * Delete all data for a tool in a workspace (used when uninstalling)
   */
  async deleteAllToolData(bookId, toolSlug, workspaceId = 'default') {
    try {
      const dataRef = collection(db, 'pb-books', bookId, 'tool-data', toolSlug, 'items');
      
      // Only delete items belonging to this workspace
      const q = query(dataRef, where('workspaceId', '==', workspaceId));
      const snapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      snapshot.docs.forEach(docSnap => {
        batch.delete(docSnap.ref);
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error(`Error deleting all ${toolSlug} data:`, error);
      throw error;
    }
  },

  /**
   * Get tool statistics
   */
  async getToolStats(bookId, toolSlug, workspaceId = 'default') {
    try {
      const items = await this.getToolData(bookId, toolSlug, workspaceId);
      
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());

      const stats = {
        total: items.length,
        thisMonth: 0,
        thisWeek: 0,
        byStatus: {}
      };

      items.forEach(item => {
        const createdAt = item.createdAt?.toDate?.() || new Date(item.createdAt);
        
        if (createdAt >= startOfMonth) stats.thisMonth++;
        if (createdAt >= startOfWeek) stats.thisWeek++;
        
        if (item.status) {
          stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting tool stats:', error);
      return { total: 0, thisMonth: 0, thisWeek: 0, byStatus: {} };
    }
  },

  // ============================================================
  // TOOL SETTINGS - Per-Installation Settings
  // ============================================================

  async getToolSettings(bookId, toolSlug) {
    try {
      const settingsRef = doc(db, 'pb-books', bookId, 'tool-settings', toolSlug);
      const docSnap = await getDoc(settingsRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return this.getDefaultToolSettings(toolSlug);
    } catch (error) {
      console.error('Error getting tool settings:', error);
      return this.getDefaultToolSettings(toolSlug);
    }
  },

  async saveToolSettings(bookId, toolSlug, settings) {
    try {
      const settingsRef = doc(db, 'pb-books', bookId, 'tool-settings', toolSlug);
      await setDoc(settingsRef, {
        ...settings,
        toolSlug,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error saving tool settings:', error);
      throw error;
    }
  },

  getDefaultToolSettings(toolSlug) {
    const defaults = {
      'sse-assessments': {
        defaultTerm: 'Term 1',
        requireTeamMembers: true
      },
      'sip-plans': {
        maxObjectivesSectionA: 8,
        enableQuarterlyReports: true
      },
      'invoices': {
        prefix: 'INV',
        currency: 'ZAR',
        taxRate: 15,
        paymentTerms: 30
      },
      'budgets': {
        currency: 'ZAR',
        fiscalYearStart: 'January'
      }
    };
    return defaults[toolSlug] || {};
  },

  // ============================================================
  // STATISTICS & ANALYTICS
  // ============================================================

  async getToolStats(bookId, toolSlug) {
    try {
      const items = await this.getToolData(bookId, toolSlug);
      
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());

      const stats = {
        total: items.length,
        thisMonth: 0,
        thisWeek: 0,
        byStatus: {}
      };

      items.forEach(item => {
        const createdAt = item.createdAt?.toDate?.() || new Date(item.createdAt);
        
        if (createdAt >= startOfMonth) stats.thisMonth++;
        if (createdAt >= startOfWeek) stats.thisWeek++;
        
        if (item.status) {
          stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting tool stats:', error);
      return { total: 0, thisMonth: 0, thisWeek: 0, byStatus: {} };
    }
  },

  // ============================================================
  // EXPORT
  // ============================================================

  async exportToolData(bookId, toolSlug, format = 'json') {
    try {
      const items = await this.getToolData(bookId, toolSlug);
      
      if (format === 'json') {
        return JSON.stringify(items, null, 2);
      }
      
      if (format === 'csv' && items.length > 0) {
        const headers = Object.keys(items[0]).filter(k => 
          !['createdAt', 'updatedAt', 'createdBy'].includes(k)
        );
        
        const csvRows = [headers.join(',')];
        items.forEach(item => {
          const values = headers.map(h => {
            const val = item[h];
            if (typeof val === 'object') return JSON.stringify(val);
            return `"${String(val || '').replace(/"/g, '""')}"`;
          });
          csvRows.push(values.join(','));
        });
        
        return csvRows.join('\n');
      }
      
      return items;
    } catch (error) {
      console.error('Error exporting tool data:', error);
      throw error;
    }
  }
};

export default toolsService;